import axios from 'axios';
import type { PreFlightResponse, BuilderData, AppTag, AppType, SlidesFromUrlResponse } from '@optidev/shared-types';
import { createAuthHeaders } from '../api-client';
import { firebaseAuth } from '../firebase';
import { markStepIfNeeded } from './onboarding';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface OnboardingFlow {
  id: string;
  name: string;
  config: FlowConfig;
}

export interface FlowConfig {
  type: 'just_prompt' | 'prompt_with_questions' | 'prompt_with_review';
  steps: FlowStep[];
  options?: {
    showReview?: boolean;
    allowRejection?: boolean;
    maxQuestions?: number;
    collectReferences?: boolean;
    collectImages?: boolean;
    collectConnections?: boolean;
  };
}

export interface FlowStep {
  id: string;
  type: 'question' | 'file_upload' | 'connection_setup' | 'review' | 'project_name' | 'custom';
  order: number;
  config: Record<string, unknown>;
  skipIf?: string;
}

export interface UploadedFile {
  fileName: string;
  fileType: string;
  downloadUrl: string;
}

export interface CompleteBuilderDto {
  flowId?: string;
  slug: string;
  builderData: BuilderData;
  templateId?: string;
}

export interface CompleteBuilderResponse {
  projectId: string;
  projectName: string;
  redirect: string;
}

export { PreFlightResponse, BuilderData, AppTag, AppType, SlidesFromUrlResponse };

export const builderApi = {
  /**
   * Get a flow for the current user (weighted random selection)
   */
  async getFlow(token: string): Promise<OnboardingFlow> {
    const response = await axios.get(`${API_URL}/api/builder/flow`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Pre-flight check - validate and classify user request
   * Returns pass/fail status, plan readiness, and classification
   */
  async preFlight(
    token: string,
    data: {
      prompt: string;
      uploadedFiles?: UploadedFile[];
    }
  ): Promise<PreFlightResponse> {
    const response = await axios.post(`${API_URL}/api/builder/pre-flight`, data, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Complete the builder flow and create a project
   */
  async complete(
    token: string,
    dto: CompleteBuilderDto,
  ): Promise<CompleteBuilderResponse> {
    const response = await axios.post(`${API_URL}/api/builder/complete`, dto, {
      headers: createAuthHeaders(token),
    });
    // Fire-and-forget onboarding step 1 mark
    const uid = firebaseAuth.currentUser?.uid;
    if (uid) markStepIfNeeded('step1', token, uid);
    return response.data;
  },

  /**
   * Generate a slides prompt from a URL
   */
  async slidesFromUrl(token: string, url: string): Promise<SlidesFromUrlResponse> {
    const response = await axios.post(`${API_URL}/api/builder/slides-from-url`, { url }, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Validate slug format (no uniqueness check - suffix guarantees uniqueness)
   */
  async validateSlug(
    token: string,
    slug: string,
  ): Promise<{ isValid: boolean; errors: string[]; slug: string }> {
    const response = await axios.post(
      `${API_URL}/api/builder/validate-slug`,
      { slug },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },
};
